import { Form } from "antd";
import PrimaryButton from "components/Buttons/PrimaryButton/PrimaryButton";
import CustomDrawer from "components/CustomDrawer/CustomDrawer";
import TextInput from "components/Inputs/TextInput/TextInput";
import Selector from "components/Selector/Selector";
import { getDw } from "helperFunctions/audience";
import useMessage from "hooks/useMessage";
import { t } from "i18next";
import GeoFencing from "pages/Audience/AudienceCreate/components/UserTrait/GeoFencing/GeoFencing";
import React, { FC, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchGeoFencingList } from "redux/features/audience/geoFencingListSlice";
import centralApi from "services/centralApi";
import { API_ENDPOINTS, METHODS } from "utils/constants";
import { IntervalEnum } from "utils/enums";
import { FilterInfo, GeoFencingModel } from "utils/models";

interface GeoFenceConditionProps {
  index: number;
  parentIndex: number;
  condition: FilterInfo;
  handleFormChange: (data: {
    [key: string]: string | string[] | boolean | number | undefined;
  }) => void;
}

const GeoFenceCondition: FC<GeoFenceConditionProps> = ({
  index,
  parentIndex,
  condition,
  handleFormChange,
}) => {
  const geoFences = useSelector((state: any) => state.geoFencingList.data);
  const [geoFencingDrawer, setGeoFencingDrawing] = useState(false);
  const loginUser = useSelector((state: any) => state.loginUser.data);
  const { appId } = useSelector((state: any) => state.activeApp);
  const { showError } = useMessage();

  const dispatch: any = useDispatch();
  //save geo fencing
  const handleGeoFencingSave = async (data: GeoFencingModel) => {
    try {
      //serialize data
      const geoFencing = new GeoFencingModel(
        data.name,
        data.desc,
        data.details
      );
      const jsonData = geoFencing.toString();
      //payload
      const params = {
        api_key: loginUser.api_key,
        app_id: appId,
        args: jsonData,
        method: METHODS.SAVE_GEO_FENCING,
      };
      //call api
      await centralApi("GET", API_ENDPOINTS.SAVE_GEO_FENCING_URL, null, params);
      //refetch geo fence data using redux
      dispatch(
        fetchGeoFencingList({ api_key: loginUser.api_key, app_id: appId })
      );
      //close geo fence drawer
      setGeoFencingDrawing(false);
    } catch (error) {
      //show message
      showError(t("somethingWentWrongMsg"));
      setGeoFencingDrawing(false);
    }
  };
  return (
    <div className="d-flex gap-2">
      {/* geofence drawer */}
      <CustomDrawer
        placement="right"
        onClose={() => setGeoFencingDrawing(false)}
        destroyOnClose
        size={"large"}
        open={geoFencingDrawer}
      >
        <GeoFencing setGeoInfo={handleGeoFencingSave} />
      </CustomDrawer>
      <Form.Item
        initialValue={condition?.value?.[0]}
        name={`value[${parentIndex}][${index}]`}
        className="input-size-md m-0"
        rules={[{required:true,message:`${t("selectValueMsg")}`}]}
      >
        <Selector
          dropdownRender={(menu) => (
            <>
              <PrimaryButton
                onClick={() => setGeoFencingDrawing(true)}
                type="link"
              >
                {t("createNewLabel")}
              </PrimaryButton>

              {menu}
            </>
          )}
          onChange={(value) => {
            const selectedFence = geoFences?.find(
              ({ _id }: { _id: string }) => _id == value
            );
            handleFormChange({
              value: [value],
              details: selectedFence.details,
            });
          }}
          options={geoFences?.map(
            ({ _id, name }: { _id: string; name: string }) => ({
              label: name,
              value: _id,
            })
          )}
        />
      </Form.Item>
      <Form.Item
        className="m-0 input-size-sm"
        rules={[{ required: true, message: `${t("dwellLabel")}` }]}
        name={`dwell[${parentIndex}][${index}]`}
        initialValue={condition.dwell}
      >
        <TextInput
        placeholder={`${t("dwellLabel")}`}
          onChange={(e: any) => {
            handleFormChange({
              dwell: parseInt(e.target.value),
              interval: undefined,
            });
          }}
          type="number"
          min={0}
        ></TextInput>
      </Form.Item>
      <Form.Item
        rules={[{ required: true, message: `${t("intervalLabel")}` }]}
        name={`interval[${parentIndex}][${index}]`}
        className="m-0 input-size-sm"
        initialValue={condition.interval}
      >
        <Selector
          onChange={(value) => {
            handleFormChange({
              dw: condition.dwell ? getDw(value, condition.dwell) : undefined,
              interval: value,
            });
          }}
          options={[
            {
              label: t("minutesLabel"),
              value: IntervalEnum.MINUTES,
            },
            {
              label: t("hoursLabel"),
              value: IntervalEnum.HOURS,
            },
            {
              label: t("dayLabel"),
              value: IntervalEnum.DAYS,
            },
          ]}
        />
      </Form.Item>
    </div>
  );
};

export default GeoFenceCondition;
